/**
 * Pure SED amendment path validation, mutation, lifecycle projection, revert
 * lineage resolution, and candidate-body admission checks.
 *
 * Pure module: zero browser, Firebase, or network (`authedFetch`) imports so
 * both client and server workflows share the exact same validation and
 * lineage rules.
 */

import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { SEDAmendment } from '../../types/sedAmendments';
import { getBindingForSedPath } from '../../services/siteContext/sedBindingRegistry';
import { REQUIREMENT_TO_FIELDS } from '../evidenceLineage/requirementToFields';
import { sedValuesEqual } from './sedValueEquality';

const PROTOTYPE_POLLUTION_SEGMENTS = new Set(['__proto__', 'constructor', 'prototype']);
const UNKNOWN_PLACEHOLDER_RE = /^(?:unknown|tbd|n\/a|none|unspecified|-)$/i;

/**
 * Explicit allow-list of SED sections whose `.data` payload contains writable
 * evidence leaves. Root metadata (`id`, `version`, `siteName`, `amendments`,
 * etc.) and read-only sections (`publicSentiment`, `projectIntent`,
 * `fullProjectIntent`) are excluded.
 */
export const WRITABLE_SED_SECTION_IDS: ReadonlySet<string> = new Set([
  'climate',
  'waterStress',
  'waterResource',
  'geotechnical',
  'survey',
  'mineralResources',
  'culturalResources',
  'seismic',
  'grid',
  'flood',
  'environmental',
  'nuclearInfra',
  'solar',
  'wind',
  'electricity',
  'airQuality',
  'connectivity',
  'parcel',
  'regulatory',
  'siteResearch',
  'siteProfile',
  'onSiteInfrastructure',
  'cooling',
  'power',
  'firmCapacity',
  'lcoe',
  'fuelSupply',
  'technology',
  'interconnection',
  'transmissionHeadroom',
  'multiHazard',
  'project',
  'water',
  'financial',
]);

/**
 * Internal or diagnostic keys inside `<section>.data` that are never user-editable
 * evidence leaves even when present on a populated SED object.
 */
const NON_WRITABLE_DATA_KEYS: ReadonlySet<string> = new Set([
  'elevationStdev',
  'elevationPointsSampled',
  'sourceKmzDocIds',
  'layerCount',
  'parsedAt',
  'warnings',
  'wetlandSourceAcreageConflicts',
  'wetlandAcreageIssues',
  'monthlySummary',
  'substations',
  'corridors',
  'waterFeatures',
  'otherPoints',
  'listedProperties',
]);

/**
 * Closed nested writable containers inside `<section>.data`.
 * Note: `culturalResources.publicRegistryScreen` is intentionally excluded
 * because public registry screen counts are read-only (`editable: false`).
 */
const WRITABLE_NESTED_DATA_FIELDS: Readonly<Record<string, Readonly<Record<string, readonly string[]>>>> = {
  grid: {
    capacityDetail: ['mw', 'basis', 'enteredVia'],
  },
  waterResource: {
    municipalSupply: [
      'provider',
      'sourceDescription',
      'avgMgd',
      'peakMgd',
      'permittedMgd',
      'excessMgd',
      'storageGallons',
      'nearestMainMiles',
      'lineToSite',
      'connectionType',
      'notes',
    ],
    wastewaterService: [
      'provider',
      'sourceDescription',
      'avgMgd',
      'peakMgd',
      'permittedMgd',
      'excessMgd',
      'storageGallons',
      'nearestMainMiles',
      'lineToSite',
      'connectionType',
      'notes',
    ],
  },
  geotechnical: {
    professionalFacts: [
      'preparedBy',
      'reportDate',
      'bearingCapacityKPa',
      'presumptiveBearingPressureKPa',
      'soilClassification',
      'boreholeDepthM',
      'groundwaterDepthM',
      'depthToBedrockM',
      'seasonalWaterTableDepthM',
      'foundationRecommendation',
      'settlementRisk',
      'liquefactionRisk',
      'expansiveSoilRisk',
      'seismicSiteClass',
    ],
  },
  mineralResources: {
    titleFacts: [
      'preparer',
      'reportDate',
      'countyRecordReference',
      'ownershipStatus',
      'reservedCommodities',
      'activeLeases',
      'surfaceUseRestrictions',
    ],
  },
  culturalResources: {
    professionalSurvey: [
      'qualifiedProfessional',
      'surveyDate',
      'surveyLevel',
      'acreageSurveyed',
      'resourcesIdentified',
      'resourceCount',
      'eligibilityRecommendation',
      'effectFinding',
      'shpoCaseNumber',
      'sensitiveLocationWithheld',
    ],
  },
  siteResearch: {
    localInfrastructure: ['utilityProvider'],
    tribalLands: ['details'],
    statePermitting: ['keyPermits', 'estimatedTimeline'],
  },
};

/**
 * Known flat scalar evidence leaves per section (`<section>.data.<leaf>`).
 */
const KNOWN_SECTION_SCALAR_LEAVES: Readonly<Record<string, readonly string[]>> = {
  climate: ['avgTemp', 'peakTemp', 'minTemp', 'avgWetBulb', 'peakWetBulb', 'humidity', 'annualPrecipitation'],
  seismic: ['pga', 'sds', 'sd1', 'designCategory', 'riskCategory', 'siteClass'],
  grid: [
    'utilityName',
    'substationName',
    'substationDistKm',
    'voltage',
    'capacity',
    'complexity',
    'nearestTransmissionKm',
    'nearestTransmissionVoltage',
    'isoRegion',
    'estimatedQueueTime',
    'estimatedInterconnectionCost',
    'headroomMW',
  ],
  waterStress: ['bwsScore', 'bwsCategory', 'bwsLabel', 'bwsRaw', 'overallWaterRisk', 'stressLevel'],
  waterResource: ['waterRightsStatus', 'consumptiveUseAfy', 'npdesPermitId', 'aquiferDepthToWaterFt'],
  flood: ['floodZone', 'sfha', 'floodRisk', 'zoneOverlap'],
  environmental: [
    'hasCriticalHabitat',
    'species',
    'hasWetlands',
    'wetlandType',
    'wetlandAcres',
    'wetlandAcreage',
    'speciesConcerns',
  ],
  nuclearInfra: ['nearestReactorName', 'nearestReactorDistKm', 'reactorsWithin200km', 'nuclearInfraMaturity'],
  solar: ['ghi', 'dni'],
  wind: ['windSpeed100m', 'windSpeed80m', 'capacityFactor'],
  electricity: ['retailRate', 'state', 'year'],
  airQuality: ['aqi', 'category', 'primaryPollutant', 'pm25Conc'],
  connectivity: [
    'nearestIxName',
    'nearestIxDistKm',
    'ixCount50km',
    'facilityCount50km',
    'nearestFiberKm',
    'fiberBuildDistanceFt',
    'fiberProvider',
    'latencyMs',
  ],
  parcel: ['acres', 'developableAcres', 'zoning', 'landUse', 'owner'],
  regulatory: ['state', 'nuclearStance', 'stateNotes', 'stateResolution', 'nepaPathway', 'tribalLandProximity'],
  siteProfile: ['acres', 'perimeterKm', 'elevationMin', 'elevationMax', 'elevationAvg', 'slopePercent'],
  onSiteInfrastructure: [
    'nearestPipelineKm',
    'nearestPipelineOperator',
    'nearestPipelineMassFlowValue',
    'nearestPipelineMassFlowUnit',
    'nearestPipelineMassFlowBasis',
    'nearestPipelineAvailabilityPct',
    'nearestPipelineGasCompositionSummary',
  ],
  cooling: ['coolingStrategy', 'bopHeatRejection', 'coolingGeneration', 'resolvedPue', 'pueBasis'],
  power: [
    'powerBasis',
    'declaredMw',
    'pueValue',
    'powerFactor',
    'itLoadMw',
    'facilityMw',
    'apparentMva',
    'basisProvenance',
    'btmCapacityMW',
    'timeToPowerYears',
  ],
  firmCapacity: ['accreditationTarget', 'targetMw', 'accreditationNote', 'isoRegion'],
  lcoe: ['blendedLcoePerMwhUsd', 'discountRate', 'lifetimeYears', 'capacityFactorPct', 'lcoeBasis'],
  fuelSupply: [
    'naturalGasPriceUsdPerMmbtu',
    'priceSeries',
    'henryHubSpotUsdPerMmbtu',
    'period',
    'fuelCostPerMwhUsd',
    'heatRateBtuPerKwh',
    'fuelCostBasis',
  ],
  technology: [
    'reactorDesignClass',
    'reactorDesignLabel',
    'fuelClass',
    'licensingPathwaySummary',
    'nuclearInterest',
    'typicalModuleMWe',
    'typicalCodYears',
    'reactorVendorId',
    'reactorVendorLabel',
    'nrcDocketId',
  ],
  survey: [
    'standard',
    'standardYear',
    'surveyor',
    'surveyDate',
    'surveyedAcreage',
    'legalDescriptionConfirmed',
    'accessConfirmed',
    'easements',
    'rightsOfWay',
    'encroachments',
    'utilitiesObserved',
    'tableAItems',
    'floodZone',
    'contoursIncluded',
    'contourIntervalFt',
    'verticalDatum',
  ],
  interconnection: [
    'queueStatus',
    'iso',
    'queuePositionCount',
    'totalQueueMw',
    'estInterconnectionCostUsdPerKw',
  ],
  transmissionHeadroom: ['atcMw', 'hostingCapacityMw', 'nodalLmpUsdPerMwh'],
  multiHazard: ['compositeRiskRating', 'compositeRiskScore', 'compositeStatePercentile'],
  project: ['inServiceDate'],
  water: ['rightsStatus', 'stressLevel'],
  financial: ['capexTotalUsd'],
};

const REGISTERED_WRITABLE_SED_LEAF_PATHS: ReadonlySet<string> = (() => {
  const paths = new Set<string>();
  for (const [section, leaves] of Object.entries(KNOWN_SECTION_SCALAR_LEAVES)) {
    for (const leaf of leaves) {
      paths.add(`${section}.data.${leaf}`);
    }
  }
  for (const [section, containers] of Object.entries(WRITABLE_NESTED_DATA_FIELDS)) {
    for (const [container, leaves] of Object.entries(containers)) {
      for (const leaf of leaves) {
        paths.add(`${section}.data.${container}.${leaf}`);
      }
    }
  }
  for (const fieldList of Object.values(REQUIREMENT_TO_FIELDS)) {
    for (const fp of fieldList) {
      if (!fp.includes('.')) continue;
      if (fp.startsWith('publicSentiment.')) continue;
      if (fp.includes('publicRegistryScreen') || fp.endsWith('.warnings')) continue;
      const dot = fp.indexOf('.');
      const section = fp.slice(0, dot);
      const tail = fp.slice(dot + 1);
      if (!WRITABLE_SED_SECTION_IDS.has(section)) continue;
      // Skip bare container entries that have nested leaves in WRITABLE_NESTED_DATA_FIELDS
      if (WRITABLE_NESTED_DATA_FIELDS[section]?.[tail]) continue;
      const variablePath = fp.includes('.data.') ? fp : `${section}.data.${tail}`;
      paths.add(variablePath);
    }
  }
  return paths;
})();

export function isRegisteredWritableSedLeaf(variablePath: string): boolean {
  if (typeof variablePath !== 'string' || !variablePath.includes('.data.')) return false;
  if (getBindingForSedPath(variablePath) !== undefined) return true;
  return REGISTERED_WRITABLE_SED_LEAF_PATHS.has(variablePath);
}

export function isWritableSedEvidencePath(variablePath: string): boolean {
  if (typeof variablePath !== 'string') return false;
  const parts = variablePath.split('.');
  if (parts.length < 3) return false;
  const section = parts[0]!;
  if (!WRITABLE_SED_SECTION_IDS.has(section)) return false;
  if (parts[1] !== 'data') return false;
  // Reject publicRegistryScreen (read-only screening summary) and internal metadata keys
  if (section === 'culturalResources' && parts[2] === 'publicRegistryScreen') return false;
  for (let i = 2; i < parts.length; i += 1) {
    if (NON_WRITABLE_DATA_KEYS.has(parts[i]!)) return false;
  }
  return true;
}

export class SedAmendmentValidationError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = 'SedAmendmentValidationError';
    this.code = code;
  }
}

export interface SEDAmendmentLifecycleEvent {
  id: string;
  amendmentId: string;
  sedId: string;
  variablePath: string;
  fromStatus: SEDAmendment['status'];
  toStatus: SEDAmendment['status'];
  byAmendmentId?: string;
  byOperationId: string;
  actorUid: string;
  actorEmail: string;
  reviewComment?: string;
  timestamp: string;
  resultingSedVersion?: number;
  companyId?: string;
}

export function isPrototypePollutionSegment(segment: string): boolean {
  return PROTOTYPE_POLLUTION_SEGMENTS.has(segment);
}

export function isUnknownPlaceholderValue(value: unknown): boolean {
  return typeof value === 'string' && UNKNOWN_PLACEHOLDER_RE.test(value.trim());
}

/**
 * Only `'applied'`, `'superseded'`, and `'reverted'` represent amendments that
 * have actually been committed to a SED revision. `'pending'`, `'accepted'`,
 * `'approved'` (approved in review workflow, awaiting publication), and
 * `'rejected'` are uncommitted.
 */
export function isCommittedAmendmentLifecycleStatus(
  status: SEDAmendment['status'] | undefined,
): boolean {
  return status === 'applied' || status === 'superseded' || status === 'reverted';
}

/**
 * Complete canonical deep structural comparison preserving exact own-property
 * key sets (distinguishing absent properties from `{ key: undefined }` or
 * `{ key: null }`) and primitive values.
 */
export function strictCanonicalRecordEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a === 'number' && typeof b === 'number' && a === 0 && b === 0) return true;
  if (typeof a !== typeof b || a === null || b === null) return false;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!strictCanonicalRecordEqual(a[i], b[i])) return false;
    }
    return true;
  }
  const aObj = a as Record<string, unknown>;
  const bObj = b as Record<string, unknown>;
  const aKeys = Object.keys(aObj).sort();
  const bKeys = Object.keys(bObj).sort();
  if (aKeys.length !== bKeys.length) return false;
  for (let i = 0; i < aKeys.length; i += 1) {
    if (aKeys[i] !== bKeys[i]) return false;
  }
  for (const key of aKeys) {
    if (!strictCanonicalRecordEqual(aObj[key], bObj[key])) return false;
  }
  return true;
}

/**
 * Pure path & leaf validation on a SiteEvidenceDocument.
 *
 * - Rejects prototype-pollution segments (`__proto__`, `constructor`, `prototype`)
 *   and empty path segments.
 * - Retains known registry-bound (`getBindingForSedPath`) absent intermediate
 *   vivification and registered absent/unknown leaf handling where legitimate.
 * - Enforces an explicit writable evidence-leaf policy (`<section>.data.<leaf>`):
 *   existing section metadata scalars (`grid.status`, `grid.confidence`, etc.),
 *   read-only screening blocks (`publicRegistryScreen`), internal stats
 *   (`elevationStdev`), and arbitrary unregistered missing leaves under an
 *   existing `.data` object are rejected.
 * - Rejects non-object/array intermediate segments and container-overwriting or
 *   type-incompatible leaf values.
 */
export function validateAmendmentPathOnSedPure(
  sed: SiteEvidenceDocument,
  variablePath: string,
  newValue?: unknown,
): void {
  if (!sed || typeof sed !== 'object' || Array.isArray(sed)) {
    throw new SedAmendmentValidationError(
      'INVALID_SED_DOCUMENT',
      'validateAmendmentPathOnSed: SED root must be an object',
    );
  }
  if (typeof variablePath !== 'string' || variablePath.trim().length === 0) {
    throw new SedAmendmentValidationError(
      'INVALID_AMENDMENT_PATH',
      `Amendment path "${String(variablePath)}" is empty`,
    );
  }
  const parts = variablePath.split('.');
  if (parts.length < 2) {
    throw new SedAmendmentValidationError(
      'INVALID_AMENDMENT_PATH',
      `Amendment path "${variablePath}" is not deep enough (need at least domain.key)`,
    );
  }
  for (let i = 0; i < parts.length; i += 1) {
    const seg = parts[i]!;
    if (seg.trim().length === 0) {
      throw new SedAmendmentValidationError(
        'INVALID_AMENDMENT_PATH',
        `Amendment path "${variablePath}" has an empty segment at index ${i}`,
      );
    }
    if (isPrototypePollutionSegment(seg)) {
      throw new SedAmendmentValidationError(
        'PROTOTYPE_POLLUTION_PATH',
        `Amendment path "${variablePath}" contains forbidden segment "${seg}"`,
      );
    }
  }

  const binding = getBindingForSedPath(variablePath);
  const isBoundPath = binding !== undefined;
  const isRegisteredNested =
    parts.length > 3 &&
    parts[1] === 'data' &&
    Boolean(WRITABLE_NESTED_DATA_FIELDS[parts[0]!]?.[parts[2]!]);

  let target: unknown = sed;
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (target == null || typeof target !== 'object' || Array.isArray(target)) {
      throw new SedAmendmentValidationError(
        'INVALID_AMENDMENT_PATH',
        `Amendment path "${variablePath}" did not resolve (broke at segment ${i}: "${parts[i]}")`,
      );
    }
    const seg = parts[i]!;
    const child = Object.prototype.hasOwnProperty.call(target, seg)
      ? (target as Record<string, unknown>)[seg]
      : undefined;
    if (child == null) {
      const canVivifyIntermediate = i < 2 ? isBoundPath : isBoundPath || isRegisteredNested;
      if (!canVivifyIntermediate) {
        throw new SedAmendmentValidationError(
          'INVALID_AMENDMENT_PATH',
          `Amendment path "${variablePath}" did not resolve (broke at segment ${i}: "${seg}")`,
        );
      }
      target = {};
    } else if (typeof child !== 'object' || Array.isArray(child)) {
      throw new SedAmendmentValidationError(
        'INVALID_AMENDMENT_PATH',
        `Amendment path "${variablePath}" intermediate "${seg}" is not an object`,
      );
    } else {
      target = child;
    }
  }

  if (target == null || typeof target !== 'object' || Array.isArray(target)) {
    throw new SedAmendmentValidationError(
      'INVALID_AMENDMENT_PATH',
      `Amendment path "${variablePath}" parent is not an object`,
    );
  }

  // Enforce explicit writable evidence-leaf policy (`<section>.data.<leaf>`).
  if (!isWritableSedEvidencePath(variablePath)) {
    throw new SedAmendmentValidationError(
      'NON_WRITABLE_EVIDENCE_PATH',
      `Amendment path "${variablePath}" targets non-writable metadata rather than a writable "<section>.data.<field>" evidence leaf`,
    );
  }

  const leafSeg = parts[parts.length - 1]!;
  const hasExistingLeaf = Object.prototype.hasOwnProperty.call(target, leafSeg);
  const existingLeaf = hasExistingLeaf
    ? (target as Record<string, unknown>)[leafSeg]
    : undefined;

  if (!hasExistingLeaf || existingLeaf === undefined) {
    if (!isRegisteredWritableSedLeaf(variablePath)) {
      throw new SedAmendmentValidationError(
        'INVALID_AMENDMENT_PATH',
        `Amendment path "${variablePath}" did not resolve (missing leaf "${leafSeg}" is not a registered writable evidence field)`,
      );
    }
  }

  if (existingLeaf !== null && typeof existingLeaf === 'object') {
    throw new SedAmendmentValidationError(
      'INCOMPATIBLE_LEAF_TYPE',
      `Amendment path "${variablePath}" targets a structural object or array rather than a leaf value`,
    );
  }

  if (newValue !== undefined) {
    const isAllowedPrimitive =
      newValue === null ||
      typeof newValue === 'string' ||
      (typeof newValue === 'number' && Number.isFinite(newValue)) ||
      typeof newValue === 'boolean';
    if (!isAllowedPrimitive) {
      throw new SedAmendmentValidationError(
        'INCOMPATIBLE_LEAF_TYPE',
        `Amendment value for "${variablePath}" must be a finite number, string, boolean, or null`,
      );
    }

    if (
      existingLeaf !== undefined &&
      existingLeaf !== null &&
      newValue !== null &&
      !isUnknownPlaceholderValue(existingLeaf) &&
      !isUnknownPlaceholderValue(newValue)
    ) {
      if (typeof existingLeaf !== typeof newValue) {
        throw new SedAmendmentValidationError(
          'INCOMPATIBLE_LEAF_TYPE',
          `Amendment value for "${variablePath}" has incompatible leaf type (${typeof newValue} vs existing ${typeof existingLeaf})`,
        );
      }
    }
  }
}

export function readValueAtSEDPathPure(
  sed: SiteEvidenceDocument,
  variablePath: string,
): unknown {
  if (!sed || typeof sed !== 'object' || typeof variablePath !== 'string') return undefined;
  const parts = variablePath.split('.');
  if (parts.length === 0) return undefined;
  let cursor: unknown = sed;
  for (const part of parts) {
    if (!part || isPrototypePollutionSegment(part)) return undefined;
    if (cursor == null || typeof cursor !== 'object' || Array.isArray(cursor)) return undefined;
    if (!Object.prototype.hasOwnProperty.call(cursor, part)) return undefined;
    cursor = (cursor as Record<string, unknown>)[part];
  }
  return cursor;
}

export function sanitizeSedJsonPayload(sed: SiteEvidenceDocument): SiteEvidenceDocument {
  return JSON.parse(
    JSON.stringify(sed, (_, v) => {
      if (typeof v === 'number' && !Number.isFinite(v)) return null;
      if (v === undefined) return null;
      if (typeof v === 'function') return null;
      return v;
    }),
  ) as SiteEvidenceDocument;
}

/**
 * Pure in-memory application of a single SEDAmendment to a SiteEvidenceDocument.
 * Does not perform any network or storage I/O.
 */
export function applySingleAmendmentToSedPure(
  sed: SiteEvidenceDocument,
  amendment: SEDAmendment,
  options?: {
    bumpVersion?: boolean;
    targetVersion?: number;
    nowIso?: string;
  },
): SiteEvidenceDocument {
  if (!sed) {
    throw new SedAmendmentValidationError('INVALID_SED_DOCUMENT', 'applySingleAmendmentToSedPure: missing sed');
  }
  if (!amendment) {
    throw new SedAmendmentValidationError('INVALID_AMENDMENT', 'applySingleAmendmentToSedPure: missing amendment');
  }

  validateAmendmentPathOnSedPure(sed, amendment.variablePath, amendment.newValue);

  const next: SiteEvidenceDocument = JSON.parse(JSON.stringify(sed));
  const parts = amendment.variablePath.split('.');
  const isBoundPath = getBindingForSedPath(amendment.variablePath) !== undefined;
  const isRegisteredNested =
    parts.length > 3 &&
    parts[1] === 'data' &&
    Boolean(WRITABLE_NESTED_DATA_FIELDS[parts[0]!]?.[parts[2]!]);

  let target: Record<string, unknown> = next as unknown as Record<string, unknown>;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const seg = parts[i]!;
    const child = Object.prototype.hasOwnProperty.call(target, seg) ? target[seg] : undefined;
    if (child == null) {
      const canVivifyIntermediate = i < 2 ? isBoundPath : isBoundPath || isRegisteredNested;
      if (!canVivifyIntermediate) {
        throw new SedAmendmentValidationError(
          'INVALID_AMENDMENT_PATH',
          `Amendment path "${amendment.variablePath}" did not resolve (broke at segment ${i}: "${seg}")`,
        );
      }
      target[seg] = {};
    } else if (typeof child !== 'object' || Array.isArray(child)) {
      throw new SedAmendmentValidationError(
        'INVALID_AMENDMENT_PATH',
        `Amendment path "${amendment.variablePath}" intermediate "${seg}" is not an object`,
      );
    }
    target = target[seg] as Record<string, unknown>;
  }

  const leafSeg = parts[parts.length - 1]!;
  target[leafSeg] = amendment.newValue;

  if (!Array.isArray(next.amendments)) next.amendments = [];
  next.amendments.push({
    field: amendment.variablePath,
    originalValue: amendment.oldValue as string | number | boolean | null,
    correctedValue: amendment.newValue as string | number | boolean | null,
    correctedBy: amendment.createdByEmail,
    reason: amendment.reason,
    source: amendment.source.label,
    timestamp: amendment.createdAt,
    ...(amendment.operationId ? { operationId: amendment.operationId } : {}),
    ...(amendment.supersedesAmendmentId ? { supersedesAmendmentId: amendment.supersedesAmendmentId } : {}),
    ...(amendment.revertedFromAmendmentId ? { revertedFromAmendmentId: amendment.revertedFromAmendmentId } : {}),
  } as SiteEvidenceDocument['amendments'][number]);

  if (options?.targetVersion !== undefined) {
    next.version = options.targetVersion;
  } else if (options?.bumpVersion !== false) {
    next.version = (sed.version ?? 1) + 1;
  }
  next.collectedAt = options?.nowIso ?? new Date().toISOString();

  return sanitizeSedJsonPayload(next);
}

/**
 * Monotonic status precedence for SEDAmendment lifecycle progression:
 * pending/accepted (1) -> approved (2) -> applied (3) -> rejected (4) -> superseded (5) -> reverted (6)
 */
export function getSedAmendmentStatusPrecedence(
  status: SEDAmendment['status'] | undefined,
): number {
  switch (status) {
    case 'reverted':
      return 6;
    case 'superseded':
      return 5;
    case 'rejected':
      return 4;
    case 'applied':
      return 3;
    case 'approved':
      return 2;
    case 'accepted':
    case 'pending':
      return 1;
    default:
      return 0;
  }
}

function isValidAmendmentLifecycleEventTransition(
  currentStatus: SEDAmendment['status'],
  fromStatus: SEDAmendment['status'],
  toStatus: SEDAmendment['status'],
): boolean {
  if (fromStatus && fromStatus !== currentStatus) {
    if (toStatus === currentStatus) return true;
  }
  switch (currentStatus) {
    case 'pending':
    case 'accepted':
      return toStatus === 'approved' || toStatus === 'rejected' || toStatus === 'applied';
    case 'approved':
      return toStatus === 'applied' || toStatus === 'rejected';
    case 'applied':
      return toStatus === 'superseded' || toStatus === 'reverted';
    case 'superseded':
      return toStatus === 'reverted';
    case 'rejected':
    case 'reverted':
      return false;
    default:
      return false;
  }
}

/**
 * Derive the authoritative effective `SEDAmendment[]` list from immutable
 * original records and append-only lifecycle transition events.
 *
 * - For every record present in `originals`, the immutable original plus the
 *   validated `lifecycleEvents` ledger is 100% authoritative. `siteHistory`
 *   projections are NEVER merged into `originals` (so a `pending` original
 *   plus a stale/forged `applied` projection without a committed `applied`
 *   event remains `pending`, a projected `reverted` cannot block lower-ranked
 *   valid events, and lineage/version fields absent from `originals` are never
 *   inherited from projections).
 * - `siteHistory` is consulted ONLY as an explicitly distinguished legacy
 *   fallback (`allowLegacyProjectionFallback: true`) for pre-migration records
 *   whose IDs are absent from `originals` and `lifecycleEvents` (and only when
 *   `originals` itself is empty/unpopulated for that ID); even in legacy
 *   fallback, an uncommitted status (`pending` / `approved`) is never promoted
 *   to `applied` by a duplicate projection entry.
 */
export function deriveAuthoritativeSedAmendments(inputs: {
  originals?: readonly SEDAmendment[];
  lifecycleEvents?: readonly SEDAmendmentLifecycleEvent[];
  siteHistory?: readonly SEDAmendment[];
  sedId?: string;
  allowLegacyProjectionFallback?: boolean;
}): SEDAmendment[] {
  const byId = new Map<string, SEDAmendment>();
  const authoritativeOriginalIds = new Set<string>();

  for (const orig of inputs.originals ?? []) {
    if (!orig || typeof orig.id !== 'string' || orig.id.trim().length === 0) continue;
    if (inputs.sedId && orig.sedId && orig.sedId !== inputs.sedId) continue;
    authoritativeOriginalIds.add(orig.id);
    byId.set(orig.id, { ...orig });
  }

  const sortedEvents = [...(inputs.lifecycleEvents ?? [])].sort((a, b) => {
    const timeDiff = Date.parse(a.timestamp) - Date.parse(b.timestamp);
    if (Number.isFinite(timeDiff) && timeDiff !== 0) return timeDiff;
    return getSedAmendmentStatusPrecedence(a.toStatus) - getSedAmendmentStatusPrecedence(b.toStatus);
  });

  for (const ev of sortedEvents) {
    if (!ev || typeof ev.amendmentId !== 'string' || !ev.byOperationId || !ev.actorUid) continue;
    const current = byId.get(ev.amendmentId);
    if (!current) continue;
    if (inputs.sedId && ev.sedId && ev.sedId !== inputs.sedId) continue;
    if (ev.sedId && current.sedId && ev.sedId !== current.sedId) continue;
    if (ev.variablePath && current.variablePath && ev.variablePath !== current.variablePath) continue;
    if (!isValidAmendmentLifecycleEventTransition(current.status, ev.fromStatus, ev.toStatus)) {
      continue;
    }
    byId.set(ev.amendmentId, {
      ...current,
      status: ev.toStatus,
      ...(ev.toStatus === 'approved' || ev.toStatus === 'rejected'
        ? {
            reviewedBy: ev.actorEmail || ev.actorUid,
            reviewedAt: ev.timestamp,
            ...(ev.reviewComment !== undefined ? { reviewComment: ev.reviewComment } : {}),
          }
        : {}),
      ...(ev.toStatus === 'applied'
        ? {
            appliedBy: ev.actorUid,
            appliedAt: ev.timestamp,
            ...(typeof ev.resultingSedVersion === 'number'
              ? { sedVersion: ev.resultingSedVersion }
              : {}),
          }
        : {}),
    });
  }

  if (inputs.allowLegacyProjectionFallback === true) {
    const eventsByAmendmentId = new Set(sortedEvents.map((e) => e.amendmentId));
    for (const hist of inputs.siteHistory ?? []) {
      if (!hist || typeof hist.id !== 'string' || hist.id.trim().length === 0) continue;
      if (inputs.sedId && hist.sedId && hist.sedId !== inputs.sedId) continue;
      // Never allow projections to override or augment an authoritative original or event-tracked record.
      if (authoritativeOriginalIds.has(hist.id) || eventsByAmendmentId.has(hist.id)) continue;
      const existing = byId.get(hist.id);
      if (!existing) {
        byId.set(hist.id, { ...hist });
        continue;
      }
      // Among duplicate legacy projection copies:
      // 1. Any uncommitted status ('pending' | 'accepted' | 'approved') prevents promotion to 'applied'.
      // 2. Any retired status ('reverted' | 'superseded' | 'rejected') overrides a stale 'applied' copy.
      const existingUncommitted =
        existing.status === 'pending' || existing.status === 'accepted' || existing.status === 'approved';
      const incomingUncommitted =
        hist.status === 'pending' || hist.status === 'accepted' || hist.status === 'approved';
      const existingRank = getSedAmendmentStatusPrecedence(existing.status);
      const incomingRank = getSedAmendmentStatusPrecedence(hist.status);
      let winningStatus = existing.status;
      if (existingRank >= 4 || incomingRank >= 4) {
        winningStatus = incomingRank >= existingRank ? hist.status : existing.status;
      } else if (existingUncommitted || incomingUncommitted) {
        winningStatus = existingUncommitted ? existing.status : hist.status;
      } else {
        winningStatus = incomingRank >= existingRank ? hist.status : existing.status;
      }
      byId.set(hist.id, {
        ...existing,
        status: winningStatus,
      });
    }
  }

  return [...byId.values()].sort(
    (a, b) =>
      Date.parse(b.createdAt || '1970-01-01T00:00:00.000Z') -
      Date.parse(a.createdAt || '1970-01-01T00:00:00.000Z'),
  );
}

export type RevertLineageResolutionResult =
  | {
      ok: true;
      earliest: SEDAmendment;
      latest: SEDAmendment;
      allForPath: SEDAmendment[];
      supersededIds: string[];
      originalProviderValue: string | number | boolean | null;
    }
  | {
      ok: false;
      code: 'NO_APPLIED_AMENDMENTS' | 'INVALID_AMENDMENT_LINEAGE';
      error: string;
    };

/**
 * Resolve the authoritative active amendment lineage for `(sedId, variablePath)`
 * prior to a revert operation.
 *
 * - Walks `supersedesAmendmentId` across committed superseded ancestors
 *   (`A: 10->20` superseded by `B: 20->30`) so `earliest.oldValue` is `10`.
 * - Stops cleanly at prior COMMITTED revert boundaries (`closedByPriorRevertIds`).
 *   An `'approved'`, `'accepted'`, `'pending'`, or `'rejected'` revert is NOT
 *   committed and cannot close `A: 10->20` while the actual head remains `20`.
 * - Fails closed (`INVALID_AMENDMENT_LINEAGE`) on missing ancestors, foreign
 *   `sedId`/`variablePath`, uncommitted (`pending`/`accepted`/`approved`/`rejected`)
 *   ancestors, cycles (`A <-> B`), or multiple disconnected roots with
 *   inconsistent `oldValue` baselines.
 */
export function resolveRevertLineageOnSed(
  allAmendments: readonly SEDAmendment[],
  sedId: string,
  variablePath: string,
): RevertLineageResolutionResult {
  const allById = new Map<string, SEDAmendment>();
  for (const a of allAmendments) {
    if (a && typeof a.id === 'string' && a.id.trim().length > 0) {
      allById.set(a.id, a);
    }
  }

  const pathCandidates = [...allById.values()].filter(
    (a) => a.sedId === sedId && a.variablePath === variablePath,
  );
  const pathById = new Map(pathCandidates.map((a) => [a.id, a]));
  const isRevertCandidate = (a: SEDAmendment): boolean =>
    a.status === 'reverted' ||
    Boolean(a.revertedFromAmendmentId) ||
    a.id.startsWith('revert-');

  const supersededByChildIds = new Set<string>();
  for (const a of pathCandidates) {
    if (!isCommittedAmendmentLifecycleStatus(a.status)) continue;
    if (a.supersedesAmendmentId) {
      supersededByChildIds.add(a.supersedesAmendmentId);
    }
  }

  const committedReverts = pathCandidates.filter(
    (a) => isCommittedAmendmentLifecycleStatus(a.status) && isRevertCandidate(a),
  );
  const closedByPriorRevertIds = new Set<string>();
  const walkAncestorsInto = (startId: string | undefined, targetSet: Set<string>) => {
    let cur = startId;
    while (cur && !targetSet.has(cur)) {
      targetSet.add(cur);
      const node = pathById.get(cur);
      if (!node) break;
      cur = node.supersedesAmendmentId ?? node.revertedFromAmendmentId;
    }
  };
  for (const rev of committedReverts) {
    closedByPriorRevertIds.add(rev.id);
    walkAncestorsInto(rev.revertedFromAmendmentId, closedByPriorRevertIds);
    walkAncestorsInto(rev.supersedesAmendmentId, closedByPriorRevertIds);
    const revTime = Date.parse(rev.createdAt);
    for (const cand of pathCandidates) {
      if (cand.id === rev.id) continue;
      if (!isCommittedAmendmentLifecycleStatus(cand.status)) continue;
      if (
        typeof cand.sedVersion === 'number' &&
        typeof rev.sedVersion === 'number' &&
        cand.sedVersion !== rev.sedVersion
      ) {
        if (cand.sedVersion < rev.sedVersion) {
          walkAncestorsInto(cand.id, closedByPriorRevertIds);
        }
        continue;
      }
      const candTime = Date.parse(cand.createdAt);
      if (Number.isFinite(candTime) && Number.isFinite(revTime) && candTime < revTime) {
        walkAncestorsInto(cand.id, closedByPriorRevertIds);
      }
    }
  }

  const activeAppliedSeeds = pathCandidates
    .filter(
      (a) =>
        a.status === 'applied' &&
        !isRevertCandidate(a) &&
        !closedByPriorRevertIds.has(a.id),
    )
    .sort((a, b) => {
      const verDiff = (a.sedVersion ?? 0) - (b.sedVersion ?? 0);
      if (verDiff !== 0) return verDiff;
      return Date.parse(a.createdAt) - Date.parse(b.createdAt);
    });

  if (activeAppliedSeeds.length === 0) {
    return {
      ok: false,
      code: 'NO_APPLIED_AMENDMENTS',
      error: 'No applied amendments on this variable',
    };
  }

  const activeLineageMap = new Map<string, SEDAmendment>();
  for (const seed of activeAppliedSeeds) {
    activeLineageMap.set(seed.id, seed);
    let cursorId = seed.supersedesAmendmentId;
    const visited = new Set<string>([seed.id]);
    while (cursorId) {
      if (closedByPriorRevertIds.has(cursorId)) {
        break;
      }
      if (visited.has(cursorId)) {
        return {
          ok: false,
          code: 'INVALID_AMENDMENT_LINEAGE',
          error: `Cannot revert: cyclic amendment supersedes lineage detected at "${cursorId}".`,
        };
      }
      visited.add(cursorId);
      const candidateAny = allById.get(cursorId);
      if (!candidateAny) {
        return {
          ok: false,
          code: 'INVALID_AMENDMENT_LINEAGE',
          error: `Cannot revert: incomplete amendment lineage (missing superseded amendment "${cursorId}").`,
        };
      }
      if (candidateAny.sedId !== sedId || candidateAny.variablePath !== variablePath) {
        return {
          ok: false,
          code: 'INVALID_AMENDMENT_LINEAGE',
          error: `Cannot revert: superseded amendment "${cursorId}" belongs to a different SED or variable path.`,
        };
      }
      if (isRevertCandidate(candidateAny)) {
        if (!isCommittedAmendmentLifecycleStatus(candidateAny.status)) {
          return {
            ok: false,
            code: 'INVALID_AMENDMENT_LINEAGE',
            error: `Cannot revert: superseded revert record "${cursorId}" has non-committed status "${candidateAny.status}".`,
          };
        }
        break;
      }
      if (!isCommittedAmendmentLifecycleStatus(candidateAny.status)) {
        return {
          ok: false,
          code: 'INVALID_AMENDMENT_LINEAGE',
          error: `Cannot revert: ancestor amendment "${cursorId}" has non-committed status "${candidateAny.status}".`,
        };
      }
      activeLineageMap.set(candidateAny.id, candidateAny);
      cursorId = candidateAny.supersedesAmendmentId;
    }
  }

  const isAncestorInLineage = (maybeAncestorId: string, descendant: SEDAmendment): boolean => {
    let cur = descendant.supersedesAmendmentId;
    const seen = new Set<string>();
    while (cur && !seen.has(cur)) {
      if (cur === maybeAncestorId) return true;
      seen.add(cur);
      cur = pathById.get(cur)?.supersedesAmendmentId;
    }
    return false;
  };

  const allForPath = [...activeLineageMap.values()].sort((a, b) => {
    if (isAncestorInLineage(a.id, b)) return -1;
    if (isAncestorInLineage(b.id, a)) return 1;
    const aSup = supersededByChildIds.has(a.id) || a.status === 'superseded' ? 0 : 1;
    const bSup = supersededByChildIds.has(b.id) || b.status === 'superseded' ? 0 : 1;
    if (aSup !== bSup) return aSup - bSup;
    const verDiff = (a.sedVersion ?? 0) - (b.sedVersion ?? 0);
    if (verDiff !== 0) return verDiff;
    return Date.parse(a.createdAt) - Date.parse(b.createdAt);
  });

  const roots = allForPath.filter((a) => {
    const supId = a.supersedesAmendmentId;
    return !supId || closedByPriorRevertIds.has(supId) || !activeLineageMap.has(supId);
  });
  if (roots.length > 1) {
    const firstRootOldValue = roots[0]!.oldValue;
    for (let i = 1; i < roots.length; i += 1) {
      if (!sedValuesEqual(roots[i]!.oldValue, firstRootOldValue)) {
        return {
          ok: false,
          code: 'INVALID_AMENDMENT_LINEAGE',
          error: `Cannot revert: ambiguous amendment roots with conflicting provider baselines (${String(firstRootOldValue)} vs ${String(roots[i]!.oldValue)}).`,
        };
      }
    }
  }

  const earliest = allForPath[0]!;
  const latest = allForPath[allForPath.length - 1]!;
  return {
    ok: true,
    earliest,
    latest,
    allForPath,
    supersededIds: allForPath.map((a) => a.id),
    originalProviderValue: earliest.oldValue,
  };
}

export type CandidateAmendmentIntegrityResult =
  | { ok: true }
  | {
      ok: false;
      code:
        | 'SED_BODY_ID_MISMATCH'
        | 'SED_STALE_VERSION'
        | 'SED_AMENDMENT_HISTORY_MODIFIED'
        | 'SED_AMENDED_VALUE_MODIFIED';
      reason: string;
    };

/**
 * Verify that a generic/refresh client SED save preserves:
 * 1. Exact `sedId` and non-regressing `version`.
 * 2. Exact canonical in-band `sed.amendments` array (complete record comparison
 *    including `operationId`, `supersedesAmendmentId`, `revertedFromAmendmentId`,
 *    and relevant absent/present key distinctions) and optional full
 *    `candidateAmendments` records against `authoritativeAmendments`.
 * 3. Every currently active (`status === 'applied'`) amendment's effective field
 *    value at `variablePath` and exact section-level `docFieldSources` /
 *    `provenanceDetail` source qualifiers (rejecting added, removed, or modified
 *    provenance on active amended fields).
 */
export function verifyClientSedCandidatePreservesAmendments(args: {
  baselineSed: SiteEvidenceDocument;
  candidateSed: SiteEvidenceDocument;
  authoritativeAmendments: readonly SEDAmendment[];
  candidateAmendments?: readonly SEDAmendment[];
  sedId: string;
}): CandidateAmendmentIntegrityResult {
  const { baselineSed, candidateSed, authoritativeAmendments, candidateAmendments, sedId } = args;
  if (!candidateSed || typeof candidateSed !== 'object' || candidateSed.id !== sedId) {
    return {
      ok: false,
      code: 'SED_BODY_ID_MISMATCH',
      reason: `Candidate SED id "${String((candidateSed as { id?: unknown })?.id)}" does not match "${sedId}".`,
    };
  }
  const baseVersion =
    typeof baselineSed?.version === 'number' && Number.isFinite(baselineSed.version)
      ? baselineSed.version
      : 1;
  const candVersion =
    typeof candidateSed.version === 'number' && Number.isFinite(candidateSed.version)
      ? candidateSed.version
      : 0;
  if (candVersion < baseVersion) {
    return {
      ok: false,
      code: 'SED_STALE_VERSION',
      reason: `Candidate SED version (${candVersion}) is older than baseline version (${baseVersion}).`,
    };
  }

  const baseInBand = Array.isArray(baselineSed?.amendments) ? baselineSed.amendments : [];
  const candInBand = Array.isArray(candidateSed?.amendments) ? candidateSed.amendments : [];
  if (candInBand.length !== baseInBand.length) {
    return {
      ok: false,
      code: 'SED_AMENDMENT_HISTORY_MODIFIED',
      reason: `Candidate SED altered in-band amendments length (${candInBand.length} vs ${baseInBand.length}); use the server amendment command route.`,
    };
  }
  for (let i = 0; i < baseInBand.length; i += 1) {
    const b = baseInBand[i]!;
    const c = candInBand[i]!;
    if (!strictCanonicalRecordEqual(b, c)) {
      return {
        ok: false,
        code: 'SED_AMENDMENT_HISTORY_MODIFIED',
        reason: `Candidate SED modified in-band amendment row ${i} for "${b.field}"; use the server amendment command route.`,
      };
    }
  }

  if (candidateAmendments !== undefined) {
    if (candidateAmendments.length !== authoritativeAmendments.length) {
      return {
        ok: false,
        code: 'SED_AMENDMENT_HISTORY_MODIFIED',
        reason: `Candidate amendment records length (${candidateAmendments.length}) does not match authoritative history (${authoritativeAmendments.length}).`,
      };
    }
    for (let i = 0; i < authoritativeAmendments.length; i += 1) {
      if (!strictCanonicalRecordEqual(authoritativeAmendments[i], candidateAmendments[i])) {
        return {
          ok: false,
          code: 'SED_AMENDMENT_HISTORY_MODIFIED',
          reason: `Candidate amendment record ${i} ("${authoritativeAmendments[i]?.id ?? ''}") differs from authoritative record.`,
        };
      }
    }
  }

  const activeByPath = new Map<string, SEDAmendment>();
  const activeCandidates = authoritativeAmendments
    .filter(
      (a) =>
        a.sedId === sedId &&
        a.status === 'applied' &&
        !a.revertedFromAmendmentId &&
        !a.id.startsWith('revert-'),
    )
    .sort((a, b) => {
      const verDiff = (a.sedVersion ?? 0) - (b.sedVersion ?? 0);
      if (verDiff !== 0) return verDiff;
      return Date.parse(a.createdAt) - Date.parse(b.createdAt);
    });

  for (const active of activeCandidates) {
    activeByPath.set(active.variablePath, active);
  }

  const getSectionFieldSourceEntry = (
    section: Record<string, unknown> | undefined,
    containerKey: 'docFieldSources' | 'provenanceDetail' | 'meta',
    leafField: string,
  ): { present: boolean; value: unknown } => {
    if (!section || typeof section !== 'object') return { present: false, value: undefined };
    const mapObj =
      containerKey === 'docFieldSources'
        ? (section.docFieldSources as Record<string, unknown> | undefined)
        : ((section[containerKey] as Record<string, unknown> | undefined)?.docFieldSources as
            | Record<string, unknown>
            | undefined);
    if (!mapObj || typeof mapObj !== 'object') return { present: false, value: undefined };
    const present = Object.prototype.hasOwnProperty.call(mapObj, leafField);
    return { present, value: present ? mapObj[leafField] : undefined };
  };

  for (const [variablePath, activeAmendment] of activeByPath.entries()) {
    const candidateVal = readValueAtSEDPathPure(candidateSed, variablePath);
    if (!sedValuesEqual(candidateVal, activeAmendment.newValue)) {
      return {
        ok: false,
        code: 'SED_AMENDED_VALUE_MODIFIED',
        reason: `Candidate SED modified active amended value at "${variablePath}" (${JSON.stringify(candidateVal)} vs expected ${JSON.stringify(activeAmendment.newValue)}).`,
      };
    }

    const parts = variablePath.split('.');
    const sectionKey = parts[0]!;
    const leafField = parts.slice(parts[1] === 'data' ? 2 : 1).join('.');
    const baseSection = (baselineSed as unknown as Record<string, unknown>)[sectionKey] as
      | Record<string, unknown>
      | undefined;
    const candSection = (candidateSed as unknown as Record<string, unknown>)[sectionKey] as
      | Record<string, unknown>
      | undefined;
    if (leafField) {
      for (const containerKey of ['docFieldSources', 'provenanceDetail', 'meta'] as const) {
        const baseEntry = getSectionFieldSourceEntry(baseSection, containerKey, leafField);
        const candEntry = getSectionFieldSourceEntry(candSection, containerKey, leafField);
        if (
          baseEntry.present !== candEntry.present ||
          (baseEntry.present && !strictCanonicalRecordEqual(baseEntry.value, candEntry.value))
        ) {
          return {
            ok: false,
            code: 'SED_AMENDED_VALUE_MODIFIED',
            reason: `Candidate SED modified, added, or stripped source qualifiers for active amended field "${variablePath}".`,
          };
        }
      }
      const baseHasProv = Boolean(
        baseSection && Object.prototype.hasOwnProperty.call(baseSection, 'provenanceDetail'),
      );
      const candHasProv = Boolean(
        candSection && Object.prototype.hasOwnProperty.call(candSection, 'provenanceDetail'),
      );
      if (
        baseHasProv !== candHasProv ||
        (baseHasProv && !strictCanonicalRecordEqual(baseSection?.provenanceDetail, candSection?.provenanceDetail))
      ) {
        return {
          ok: false,
          code: 'SED_AMENDED_VALUE_MODIFIED',
          reason: `Candidate SED modified or added section provenanceDetail for active amended field "${variablePath}".`,
        };
      }
    }
  }

  return { ok: true };
}
